import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTable, { type Column } from '@/components/base/DataTable';
import { formatCurrency, formatDate } from '@/lib/format';
import type { SettlementRequest } from '@/types';
import SettlementStatusBadge from './SettlementStatusBadge';

interface SettlementsTableProps {
  settlements: SettlementRequest[];
  currency: string;
}

export default function SettlementsTable({ settlements, currency }: SettlementsTableProps) {
  const { t, i18n } = useTranslation();

  const processedLabel = (settlement: SettlementRequest) =>
    settlement.processedAt ? formatDate(settlement.processedAt, i18n.language) : t('finance.set.notProcessed');

  const reasonLabel = (settlement: SettlementRequest) =>
    settlement.status === 'rejected'
      ? settlement.rejectionReason ?? '—'
      : '—';

  const columns: Column<SettlementRequest>[] = [
    {
      key: 'requestedAt',
      header: t('finance.set.col.requestedAt'),
      render: (s) => <span className="text-foreground-800">{formatDate(s.requestedAt, i18n.language)}</span>,
    },
    {
      key: 'processedAt',
      header: t('finance.set.col.processedAt'),
      render: (s) => (
        <span className={s.processedAt ? 'text-foreground-800' : 'text-foreground-400'}>
          {processedLabel(s)}
        </span>
      ),
    },
    {
      key: 'amount',
      header: t('finance.set.col.amount'),
      render: (s) => (
        <span className="whitespace-nowrap font-semibold text-foreground-950">
          {formatCurrency(s.amount, currency)}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('finance.set.col.status'),
      render: (s) => <SettlementStatusBadge status={s.status} />,
    },
    {
      key: 'reason',
      header: t('finance.set.col.reason'),
      render: (s) => (
        <span
          className={
            s.status === 'rejected' ? 'block max-w-[16rem] text-foreground-700' : 'text-foreground-400'
          }
        >
          {reasonLabel(s)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-end',
      render: (s) => (
        <div className="flex justify-end">
          <Link
            to={`/dashboard/finance/settlements/${s.id}`}
            className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-eye-line text-base" aria-hidden="true" />
            {t('finance.set.view')}
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="hidden md:block">
        <DataTable columns={columns} rows={settlements} rowKey={(s) => s.id} />
      </div>

      <ul className="divide-y divide-background-100 md:hidden">
        {settlements.map((s) => (
          <li key={s.id} className="space-y-3 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="lb-ltr-field text-sm font-semibold text-foreground-950">{s.id}</p>
                <p className="mt-0.5 text-xs text-foreground-500">
                  {t('finance.set.col.requestedAt')}: {formatDate(s.requestedAt, i18n.language)}
                </p>
              </div>
              <SettlementStatusBadge status={s.status} />
            </div>

            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-foreground-500">{t('finance.set.col.amount')}</p>
                <p className="mt-0.5 text-sm font-bold text-foreground-950">
                  {formatCurrency(s.amount, currency)}
                </p>
              </div>
              <span className="text-xs text-foreground-500">
                {t('finance.set.col.processedAt')}: {processedLabel(s)}
              </span>
            </div>

            {s.status === 'rejected' && s.rejectionReason ? (
              <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-relaxed text-rose-700">
                {s.rejectionReason}
              </p>
            ) : null}

            <div className="flex justify-end border-t border-background-100 pt-3">
              <Link
                to={`/dashboard/finance/settlements/${s.id}`}
                className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-eye-line text-base" aria-hidden="true" />
                {t('finance.set.view')}
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}